import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button, Box, useMediaQuery } from '@mui/material';
import { useAccount } from '../../contexts/Account';
import {
  updateAdvisorOnboardingStep6,
  getAdvisorSuggestedOpportunities,
} from '../../services/advisor_services';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import ACSnackbar from '../../components/ACSnackbar';
import AdvisoryCloudLogo from '../../components/AdvisoryCloudLogo';
import { OnboardingProcess, OnboardingStatus } from '../../services/utils/types';

const useStyles = (isSM) => ({
  main: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  header: {
    display: 'flex',
    padding: isSM ? '16px 8px' : '24px 0px 0px',
    justifyContent: 'center',
    width: '100%',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: { xxs: '100%', xs: '400px' },
  },
});

function AOBFStep4() {
  const { accountDetails, currentPlan, editAccountInformation } = useAccount();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const classes = useStyles(isSM);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [disabledButton, setDisabledButton] = useState(true);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);
      await updateAdvisorOnboardingStep6({
        ADVISOR_ID: accountDetails.id,
        ONBOARDING_STATUS: OnboardingStatus.COMPLETE,
      });
      editAccountInformation({
        onboardingStatus: OnboardingStatus.COMPLETE,
        onboardingProcess: OnboardingProcess.COMPLETED,
        onboardingCompleted: true,
      });
      window.location.replace(currentPlan.onboardingRedirectUrl);
    } catch (error) {
      setIsLoading(false);
      setOpenSnackbar(true);
      console.error(error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDisabledButton(false);
    }, 7800);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!accountDetails?.id) return;
    getAdvisorSuggestedOpportunities({
      ADVISOR_ID: accountDetails.id,
    }).catch((err) => {
      console.error('Error fetching suggested opportunities:', err);
    });
  }, [accountDetails?.id]);

  return (
    <>
      <Helmet>
        <title>Profile Builder - AdvisoryCloud</title>
      </Helmet>

      <Box sx={classes.main}>
        <Box sx={classes.header}>
          <AdvisoryCloudLogo
            removeHyperlink
            onClick={() => {}}
            width={240}
            height={80}
            cursor="default"
          />
        </Box>

        <Box sx={classes.body}>
          <iframe
            src="/static/loading-advisor-onboarding-slower.html"
            title="Loading Profile"
            frameBorder="0"
            style={{
              width: '100%',
              height: '400px',
              overflowX: 'visible',
              marginBottom: '16px',
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={disabledButton || isLoading}
            onClick={handleSubmit}
            id="aobf-submit-button"
            sx={{
              borderRadius: '50px',
              backgroundColor: '#3171F6',
              '&:hover': { backgroundColor: '#3171F6' },
              height: '48px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 700,
              width: { xxs: '90%', sm: '350px' },
            }}
          >
            {isLoading ? <LoadingLogoSkeleton /> : 'View Matches'}
          </Button>
        </Box>
        <ACSnackbar
          onClose={() => setOpenSnackbar(false)}
          open={openSnackbar}
          severity="error"
          text="Something went wrong"
        />
      </Box>
    </>
  );
}

export default AOBFStep4;
